import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

/**
 * SplashScreen — elegant editorial loader shown once per session on first mount.
 *
 * Design: warm ivory background, animated monogram (AB) that draws in with an
 * ink-stroke effect, name in Gloock serif underneath, a slim progress rule, and
 * a fade-out exit. Respects prefers-reduced-motion.
 */
export function SplashScreen({ minDuration = 1400, onDone }) {
    const reduce = useReducedMotion();
    const [visible, setVisible] = useState(() => {
        if (typeof window === "undefined") return false;
        try {
            return sessionStorage.getItem("ab_splash_shown") !== "1";
        } catch { return true; }
    });

    useEffect(() => {
        if (!visible) { onDone?.(); return; }
        const dur = reduce ? 400 : minDuration;
        const t = setTimeout(() => {
            setVisible(false);
            try { sessionStorage.setItem("ab_splash_shown", "1"); } catch {}
            onDone?.();
        }, dur);
        return () => clearTimeout(t);
    }, [visible, minDuration, reduce, onDone]);

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    key="splash"
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }}
                    className="fixed inset-0 z-[100] bg-background text-foreground grid place-items-center overflow-hidden"
                    aria-hidden="true"
                >
                    {/* Editorial texture */}
                    <div className="absolute inset-0 paper-noise" aria-hidden />

                    {/* Corner marks (editorial print register) */}
                    <Corner className="top-6 left-6" />
                    <Corner className="top-6 right-6 rotate-90" />
                    <Corner className="bottom-6 left-6 -rotate-90" />
                    <Corner className="bottom-6 right-6 rotate-180" />

                    <div className="relative z-10 flex flex-col items-center text-center">
                        {/* Monogram "AB" mark */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.85 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                            className="relative h-24 w-24 sm:h-28 sm:w-28 grid place-items-center"
                        >
                            <svg viewBox="0 0 120 120" className="absolute inset-0 h-full w-full" fill="none" stroke="hsl(var(--forest))" strokeWidth="1.2">
                                <motion.circle
                                    cx="60" cy="60" r="56"
                                    initial={{ pathLength: 0, opacity: 0.4 }}
                                    animate={{ pathLength: 1, opacity: 1 }}
                                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                                />
                                <motion.circle
                                    cx="60" cy="60" r="48"
                                    initial={{ pathLength: 0, opacity: 0.2 }}
                                    animate={{ pathLength: 1, opacity: 0.6 }}
                                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
                                    strokeDasharray="2 4"
                                />
                            </svg>
                            <motion.span
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                className="relative font-display text-4xl sm:text-5xl tracking-tight text-foreground"
                            >
                                AB
                            </motion.span>
                        </motion.div>

                        {/* Name & title */}
                        <motion.p
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.55 }}
                            className="mt-6 font-display text-3xl sm:text-4xl tracking-tight"
                        >
                            Aditya Baheti
                        </motion.p>
                        <motion.p
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.7 }}
                            className="mt-2 font-mono text-[10px] sm:text-xs uppercase tracking-[0.28em] text-forest"
                        >
                            Academic Fellow · NLU Jodhpur
                        </motion.p>

                        {/* Progress rule */}
                        <div className="mt-8 w-40 sm:w-56 h-[2px] bg-border overflow-hidden rounded-full">
                            <motion.div
                                initial={{ scaleX: 0, transformOrigin: "0% 50%" }}
                                animate={{ scaleX: 1 }}
                                transition={{ duration: (reduce ? 0.4 : minDuration) / 1000, ease: "linear" }}
                                className="h-full bg-forest"
                            />
                        </div>
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.9 }}
                            className="mt-3 font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground"
                        >
                            Loading portfolio
                        </motion.p>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function Corner({ className = "" }) {
    return (
        <div className={`absolute h-6 w-6 ${className}`} aria-hidden>
            <div className="absolute top-0 left-0 h-px w-6 bg-foreground/30" />
            <div className="absolute top-0 left-0 h-6 w-px bg-foreground/30" />
        </div>
    );
}
